import React, { useState } from "react";
import * as styles from "@styles/Demographic.css";
import { useIoState } from "@utils/useIoState";
import validator from "validator";

import * as IO from "@ps-bridge/interop/io";
import { makeUserIndustry } from "@ps-bridge/models/brands/userIndustry";
import { makeUserTitle } from "@ps-bridge/models/brands/userTitle";
import { makeUserIntention } from "@ps-bridge/models/brands/userIntention";
import { makeUserZipcode } from "@ps-bridge/models/brands/userZipcode";
import type { DemographicSurveyPayload, DemographicSurveyViewProps } from "@ps-bridge/contracts/demographicSurvey";
import ZipcodeErrorPanel from "@view/panels/demographic/ZipcodeError.panel";
import { visibilityPhaseNamespace as visibilityPhase } from "@ps-bridge/models/ui/visibilityPhase";
import type { VisibilityPhase } from "@ps-bridge/models/ui/visibilityPhase";

const DemographicSurveyForm: React.FC<DemographicSurveyViewProps> = ({
  onSubmit,
}) => {
  const { read: userTitle, set: setUserTitle } = useIoState("");
  const { read: userIntention, set: setUserIntention } = useIoState("");
  const { read: userZipcode, set: setUserZipcode } = useIoState("");
  const { read: userIndustry, set: setUserIndustry } = useIoState("");

  const [errors, setErrors] = useState({
    industry: false,
    title: false,
    intention: false,
    zipcode: false,
    zipcodeErrorText: "",
  });
  const zipcodeErrorPhase: VisibilityPhase = errors.zipcode
    ? visibilityPhase.visible
    : visibilityPhase.hidden;

  const validateForm = (): boolean => {
    let isValid = true;
    const newErrors = {
      industry: userIndustry.trim() === "",
      title: userTitle.trim() === "",
      intention: userIntention.trim() === "",
      zipcode: false,
      zipcodeErrorText: "",
    };

    if (newErrors.title || newErrors.intention || newErrors.industry) {
      isValid = false;
    }

    // ZIP code validation: 5-digit or ZIP+4 (9-digit)
    if (
        !validator.isPostalCode(userZipcode.trim(), "US") &&
        !/^\d{5}-\d{4}$/.test(userZipcode.trim()) // ZIP+4 format check
    ) {
      newErrors.zipcode = true;
      newErrors.zipcodeErrorText =
          "Please enter a valid 5-digit or 9-digit ZIP code.";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  return (
      <div
        className={styles.mainContainer}
        data-testid="page-demographicSurvey"
        data-page-state="ready"
      >
        <div className={styles.container}>
          <p className={styles.title}>Demographic Survey</p>

          {/* Industry Field */}
          <div className={styles.formRow}>
            <label className={styles.label}>Industry:</label>
            <input
                data-testid="demographic-industry-input"
                className={`${styles.input} ${errors.title ? styles.inputError : ""}`}
                value={userIndustry}
                onChange={(event) => IO.run(setUserIndustry(event.target.value))}
                placeholder="e.g. Business Services, Real Estate, Healthcare, etc."
            />
          </div>
          {/* Title Field */}
          <div className={styles.formRow}>
            <label className={styles.label}>Title:</label>
            <input
                data-testid="demographic-title-input"
                className={`${styles.input} ${errors.title ? styles.inputError : ""}`}
                value={userTitle}
                onChange={(event) => IO.run(setUserTitle(event.target.value))}
                placeholder="e.g., Engineer, Student, etc."
            />
          </div>

          {/* Intention Field */}
          <div className={styles.formRow}>
            <label className={styles.label}>Intention:</label>
            <textarea
                data-testid="demographic-intention-input"
                className={`${styles.textarea} ${errors.intention ? styles.inputError : ""}`}
                value={userIntention}
                onChange={(event) => IO.run(setUserIntention(event.target.value))}
                placeholder="What do you want to achieve?"
            />
          </div>

          {/* Business Zipcode Field */}
          <div className={styles.formRow}>
            <label className={styles.label}>Business Zipcode:</label>
            <input
                data-testid="demographic-zipcode-input"
                className={`${styles.input} ${errors.zipcode ? styles.inputError : ""}`}
                value={userZipcode}
                onChange={(event) => IO.run(setUserZipcode(event.target.value))}
                placeholder="Enter your 5- or 9-digit zipcode"
            />
          </div>
          <ZipcodeErrorPanel
            phase={zipcodeErrorPhase}
            message={errors.zipcodeErrorText}
          />

          {/* Privacy Statement */}
          <p className={styles.privacyText}>
            This information is for internal marketing purposes only and will not
            be sold or shared with third parties.
          </p>

          {/* Submit Button */}
          <button
            data-testid="demographic-submit"
            className={styles.button}
            onClick={() => {
              if (validateForm()) {
                const demographicData: DemographicSurveyPayload = {
                  user_industry: makeUserIndustry(userIndustry),
                  user_title: makeUserTitle(userTitle),
                  user_intention: makeUserIntention(userIntention),
                  user_zipcode: makeUserZipcode(userZipcode),
                };

                IO.run(onSubmit(demographicData));
              }
            }}
          >
            Submit
          </button>
        </div>
      </div>
  );
};

export default DemographicSurveyForm;
